// AnalyticsContext.js - PostHog Analytics Provider
import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ANALYTICS_CONFIG, isAnalyticsEnabled } from '../config/analytics';

const AnalyticsContext = createContext(null);

// posthog-js is a sizeable dependency and is only needed when analytics is
// enabled, so it is loaded via a dynamic import (code-split into its own chunk)
// and initialized lazily instead of being pulled into the critical bundle.
let posthogPromise = null;
const loadPosthog = () => {
  if (!isAnalyticsEnabled()) return Promise.resolve(null);
  if (!posthogPromise) {
    posthogPromise = import('posthog-js').then((mod) => {
      const posthog = mod.default || mod;
      posthog.init(ANALYTICS_CONFIG.apiKey, ANALYTICS_CONFIG.options);
      // Preserve the global other modules (e.g. AuthContext) rely on.
      if (typeof window !== 'undefined') {
        window.posthog = posthog;
      }
      return posthog;
    });
  }
  return posthogPromise;
};

export const AnalyticsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const posthogRef = useRef(null);

  // Initialize PostHog on mount (lazily loads posthog-js)
  useEffect(() => {
    if (!isAnalyticsEnabled()) {
      console.info('Analytics disabled - set VITE_ENABLE_ANALYTICS=true to enable');
      return;
    }

    let mounted = true;
    loadPosthog().then((posthog) => {
      if (mounted) posthogRef.current = posthog;
    });

    return () => {
      mounted = false;
      if (posthogRef.current) {
        posthogRef.current.reset(); // Clear on unmount
      }
    };
  }, []);

  // Identify user when authenticated
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;

    loadPosthog().then((posthog) => {
      if (!posthog) return;
      if (currentUser) {
        // Identify user with PostHog
        posthog.identify(currentUser.id.toString(), {
          email: currentUser.email,
          username: currentUser.username,
          role: currentUser.role,
          team_ids: currentUser.teams?.map(t => t.id) || [],
          team_names: currentUser.teams?.map(t => t.name) || [],
          color: currentUser.color,
          created_at: currentUser.created_at,
          weekly_hours: currentUser.weekly_hours,
        });

        // Set super properties that will be included with all events
        posthog.register({
          user_role: currentUser.role,
          user_id: currentUser.id,
        });
      } else {
        // Clear identity on logout
        posthog.reset();
      }
    });
  }, [currentUser]);

  /**
   * Track a page view
   */
  const trackPageView = useCallback((pageName, properties = {}) => {
    if (!isAnalyticsEnabled()) return;

    const posthog = posthogRef.current;
    if (!posthog) return;
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties,
    });
  }, []);

  /**
   * Track a custom event
   */
  const trackEvent = useCallback((eventName, properties = {}) => {
    if (!isAnalyticsEnabled()) return;

    const posthog = posthogRef.current;
    if (!posthog) return;
    posthog.capture(eventName, properties);
  }, []);

  /**
   * Track timing/performance metrics
   */
  const trackTiming = useCallback((category, variable, timeMs, properties = {}) => {
    if (!isAnalyticsEnabled()) return;

    const posthog = posthogRef.current;
    if (!posthog) return;
    posthog.capture('timing', {
      category,
      variable,
      time_ms: timeMs,
      ...properties,
    });
  }, []);

  /**
   * Set a user property
   */
  const setUserProperty = useCallback((property, value) => {
    if (!isAnalyticsEnabled()) return;

    const posthog = posthogRef.current;
    if (!posthog) return;
    posthog.people.set({ [property]: value });
  }, []);

  /**
   * Start session recording manually
   */
  const startRecording = useCallback(() => {
    if (!isAnalyticsEnabled()) return;
    const posthog = posthogRef.current;
    if (!posthog) return;
    posthog.startSessionRecording();
  }, []);

  /**
   * Stop session recording manually (for sensitive operations)
   */
  const stopRecording = useCallback(() => {
    if (!isAnalyticsEnabled()) return;
    const posthog = posthogRef.current;
    if (!posthog) return;
    posthog.stopSessionRecording();
  }, []);

  const value = {
    trackPageView,
    trackEvent,
    trackTiming,
    setUserProperty,
    startRecording,
    stopRecording,
    isEnabled: isAnalyticsEnabled(),
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Hook to access analytics functions
 */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};
